import React, {useEffect, useState} from "react";
import {useServiceBase} from "../../Components/ReactTable/useServiceBase";
import Page from "../../Components/Page";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import EditableReactTableV2 from "../../Components/ReactTable/EditableReactTableV2";
import RetailerIdAndName from "../../Components/RetailerIdAndName";
import {getRetailerName} from "../../../utils/localCache/retailerMapCache";
import {EditFormDateInputField, EditFormDropdownInputField} from "../../Components/ReactTable/EditRecordForm";
import {setValuesToNullIfEmptyOrUndefined} from "../../Components/ReactTable/helpers";
import {sortAscendingWithNullsFirst} from "../../../utils/sorting";
import {makeApiCall} from "../../../Api/makeApiCall";
import CsvDownload from "../../../utils/CsvDownloadWithNulls";
import StoreParticipationBulkAdd from "./StoreParticipationBulkAdd";
import StoreParticipationBulkUpload from "./StoreParticipationBulkUpload";


function StoreParticipation() {
    const [mode, setMode] = useState('list');
    const [storesDropdown, setStoresDropdown] = useState([]);
    const [storeNamesById, setStoreNamesById] = useState({});
    const [storesLoading, setStoresLoading] = useState(true);

    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?add_retailer_column=true`
    const endpointUrlForPost = `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/`
    const {loading, data, onRowEditConfirmed, onRowDeleteConfirmed, refreshData} = useServiceBase({endpointUrl, endpointUrlForPost});

    useEffect(() => {
        makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/stores/`).then((storesResult) => {
            setStoresDropdown(storesResult.data.map(store => (
                {label: `${store.storeId} - ${store.storeName}`, value: store.storeId}
            )));
            setStoreNamesById(Object.fromEntries(storesResult.data.map(store => [store.storeId, store.storeName])));
        }).finally(() => setStoresLoading(false));
    }, []);

    const columns = [
        {
            header: 'Retailer',
            id: 'retailerIdName',
            filterable: true,
            accessorKey: 'retailerId',
            size: 200,
            cell: ({row}) => <RetailerIdAndName retailerId={row.original?.Retailer?.retailerId}/>,
            filterFn: (row, columnIds, filterValue) => {
                const retailerId = row.original?.Retailer?.retailerId;
                return !filterValue || Boolean(`${retailerId} ${getRetailerName(retailerId)?.toLowerCase()}`.includes(filterValue.toLowerCase()));
            },
            sortingFn: (a, b) => {
                const valA = a.original?.Retailer?.retailerId;
                const valB = b.original?.Retailer?.retailerId;
                return sortAscendingWithNullsFirst(valA, valB);
            }
        },
        {header: "Store ID", accessorKey: "storeId"},
        {header: "Store Name", id: "storeName", accessorFn: row => storeNamesById[row.storeId]},
        {header: "Stop Date", accessorKey: "stopDate"},
        {header: "Start Date", accessorKey: "startDate"}
    ];

    function onRowEditConfirmedWrapper(newOrUpdatedRow) {
        const sanitizedData = setValuesToNullIfEmptyOrUndefined(newOrUpdatedRow);
        delete sanitizedData.Retailer
        return onRowEditConfirmed(sanitizedData);
    }

    function onBulkAddDone() {
        setMode('list');
        refreshData();
    }

    function downloadCsv() {
        const rows = (data.data || []).map(row => ({
            storeId: row.storeId,
            storeName: storeNamesById[row.storeId],
            stopDate: row.stopDate,
            startDate: row.startDate
        }));
        CsvDownload(rows, 'storeParticipation.csv');
    }

    const breadCrumbItems = mode === 'list'
        ? [{label: "Configurations"}, {label: "Store"}, {label: "Store Participation"}]
        : [{label: "Configurations"}, {label: "Store"}, {label: "Store Participation", to: "/storeParticipation"}, {label: "Bulk Add"}];

    return (<Page name={"Store Participation"}
                  breadCrumbs={<Breadcrumbs items={breadCrumbItems}/>}
                  headerSubComponent={mode === 'list' &&
                      <div className={'store-participation-header-buttons'}>
                          <a className={"button primary"}
                             data-testid={"store-participation-bulk-edit-link"}
                             href={"/storeParticipation/bulkEdit"}>
                              Bulk Edit
                          </a>
                          <button type={"button"} className={"button primary"}
                                  data-testid={"store-participation-bulk-add-button"}
                                  onClick={() => setMode('bulkAdd')}>
                              Bulk Add
                          </button>
                          <StoreParticipationBulkUpload onSubmitted={refreshData} storeNamesById={storeNamesById}/>
                          <button type={"button"} className={"hollow button secondary"}
                                  data-testid={"store-participation-download-csv-button"}
                                  onClick={downloadCsv}>
                              Download CSV
                          </button>
                      </div>}
    >
        <LoadingWrapper loading={loading || storesLoading}>
            {mode === 'list' &&
                <EditableReactTableV2
                    filterable
                    data={data.data}
                    columns={columns}
                    editTable={[
                        {id: 'storeId', Component: args => <EditFormDropdownInputField {...args} options={storesDropdown} isRequired/>},
                        {id: 'stopDate', Component: args => <EditFormDateInputField {...args} isRequired/>},
                        {id: 'startDate', Component: args => <EditFormDateInputField {...args} />},
                    ]}
                    onRowEditConfirmed={onRowEditConfirmedWrapper}
                    onRowDeleteConfirmed={onRowDeleteConfirmed}
                />
            }
            {mode === 'bulkAdd' &&
                <StoreParticipationBulkAdd
                    onCancel={() => setMode('list')}
                    onSubmitted={onBulkAddDone}
                />
            }
        </LoadingWrapper>
    </Page>);
}

export default StoreParticipation;
